import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useEditor, useNode } from '@splashthat/splash-cms-engine';
import { useSessions } from 'common/components/TouchpointEditor/contexts/SessionsContext';

import { getInstance as getBootstrap } from 'utils/bootstrap';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';
import { BRAND_LIBRARY_DARK_TEXT_COLOR, BRAND_LIBRARY_BODY_TEXT } from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import {
    useSessionRegistrationButtonsStateMachine,
    POST_EVENT_REGISTRATION,
} from 'common/compounds/AgendaTouchpoint/contexts/SessionRegistrationStateProvider';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';
import { usePreview } from 'common/components/TouchpointEditor/hooks/usePreview';
import { StyledTabsTrigger, StyledTabsList } from './SessionTab.styles';

const flags = getBootstrap().get('flags', {});

export default function SessionTab({
    styles: { fontColor, fontColorActive, fontSize, fontFamily, fontSizeActive, fontFamilyActive },
    meta,
}) {
    const editor = useEditor((state) => ({
        editing: state.editing,
    }));
    const {
        event,
        theme: { activeFonts, colors },
    } = useTouchpointData();
    const { guestAccessHash } = useGuestAccessHash(`sessionsRegistration/${event?.id}/guestAccessHash`);
    const isAttendee = !!guestAccessHash;

    const { shouldPreview } = usePreview({ event });
    const { agendaPreviewState } = useSessionRegistrationButtonsStateMachine();
    const isAttendeeOrEditing =
        isAttendee || editor.editing || (shouldPreview && agendaPreviewState === POST_EVENT_REGISTRATION);

    const node = useNode();
    const customCssClassName = getCustomCssClassName(node);
    const sessions = useSessions();
    const doesAnySessionRequireRegistration = useMemo(
        () => sessions.data.some((session) => session.registrationRequired),
        [sessions.data],
    );
    const allSessionsCount = sessions.filteredData.length;
    const attendeeSessionsCount = sessions.filteredGuestSessionsData.length;
    const shouldShowMySessionsTab = doesAnySessionRequireRegistration && isAttendeeOrEditing;
    return (
        <>
            {shouldShowMySessionsTab && (
                <StyledTabsList
                    onMouseLeave={node.actions.onMouseLeave()}
                    onMouseMove={node.actions.onMouseMove()}
                    onClick={node.actions.onClick()}
                    className={customCssClassName}
                >
                    <StyledTabsTrigger
                        $fontFamilyActive={activeFonts[fontFamilyActive]}
                        $fontFamily={activeFonts[fontFamily]}
                        $fontColor={colors[fontColor]}
                        $fontColorActive={colors[fontColorActive]}
                        $fontSize={fontSize}
                        $fontSizeActive={fontSizeActive}
                        value="allSessions"
                    >
                        {meta.allSessionsText} ({allSessionsCount})
                    </StyledTabsTrigger>
                    <StyledTabsTrigger
                        $fontFamilyActive={activeFonts[fontFamilyActive]}
                        $fontFamily={activeFonts[fontFamily]}
                        $fontColor={colors[fontColor]}
                        $fontColorActive={colors[fontColorActive]}
                        $fontSize={fontSize}
                        $fontSizeActive={fontSizeActive}
                        value="mySessions"
                    >
                        {meta.mySessionsText} ({attendeeSessionsCount})
                    </StyledTabsTrigger>
                </StyledTabsList>
            )}
        </>
    );
}

SessionTab.splash = {
    related: {
        settings: React.lazy(() => import('./SessionTab.tools')),
    },
    defaultProps: {
        styles: {
            fontFamily: BRAND_LIBRARY_BODY_TEXT,
            fontSize: 16,
            fontColor: BRAND_LIBRARY_DARK_TEXT_COLOR,
            fontFamilyActive: BRAND_LIBRARY_BODY_TEXT,
            fontSizeActive: 16,
            fontColorActive: BRAND_LIBRARY_DARK_TEXT_COLOR,
        },
        meta: {
            allSessionsText: 'All Sessions',
            mySessionsText: flags.isSessionVisibilityEnabled ? 'My Agenda' : 'My Sessions',
        },
    },
};

SessionTab.propTypes = {
    styles: PropTypes.shape({
        fontFamily: PropTypes.number,
        fontSize: PropTypes.number,
        fontColor: PropTypes.number,
        fontFamilyActive: PropTypes.number,
        fontSizeActive: PropTypes.number,
        fontColorActive: PropTypes.number,
    }).isRequired,
    meta: PropTypes.shape({
        allSessionsText: PropTypes.string,
        mySessionsText: PropTypes.string,
    }).isRequired,
};
